import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, LogOut, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from './ThemeToggle';

export default function RefundCancellationPolicyPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user, logout, openLogin } = useAuth();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const currentYear = new Date().getFullYear();

  const theme = {
    bg: isDark ? '#0f172a' : '#f3f4f6',
    panelBg: isDark ? 'rgba(12,10,8,0.86)' : 'rgba(255,255,255,0.9)',
    border: isDark ? 'rgba(148,163,184,0.22)' : '#e2e8f0',
    textPrimary: isDark ? 'rgba(255,255,255,0.92)' : '#111827',
    textSecondary: isDark ? 'rgba(255,255,255,0.72)' : '#4b5563',
    textMuted: isDark ? 'rgba(255,255,255,0.56)' : '#9ca3af',
    accent: isDark ? '#F59E0B' : '#2563eb',
  };

  useEffect(() => {
    const fn = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  return (
    <div className={isDark ? 'dark-app' : 'light-app'} style={{ fontFamily: "'Manrope', sans-serif", background: theme.bg, color: theme.textPrimary, minHeight: '100vh' }}>
      <header
        className="nav-container"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: theme.panelBg,
          backdropFilter: 'blur(16px)',
          borderBottom: `1px solid ${theme.border}`,
          padding: '10px 24px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button
            type="button"
            onClick={() => navigate('/')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: theme.textSecondary, cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0 }}
            onMouseEnter={e => e.currentTarget.style.color = theme.textPrimary}
            onMouseLeave={e => e.currentTarget.style.color = theme.textSecondary}
          >
            <ArrowLeft size={14} />
            Back
          </button>
          <div style={{ width: 1, height: 18, background: theme.border }} />
          <div onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <div style={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', borderRadius: '8px', padding: '4px', border: isDark ? 'none' : '1px solid #e2e8f0' }}>
              <img src="/assets/logo.png" alt="fairhire logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <span style={{ fontSize: 18, fontWeight: 800, color: theme.textPrimary, fontFamily: "'Playfair Display', serif" }}>fairhire</span>
          </div>
        </div>

        <div style={{ position: 'relative' }} ref={profileMenuRef}>
          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ThemeToggle />
              <div style={{ position: 'relative' }}>
                <button
                  data-testid="profile-icon"
                  onClick={() => setIsProfileMenuOpen(v => !v)}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(37,99,235,0.1)',
                    border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(37,99,235,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <UserIcon size={16} color={isDark ? 'rgba(255,255,255,0.8)' : '#2563eb'} />
                </button>
                <AnimatePresence>
                  {isProfileMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 'calc(100% + 8px)',
                        width: 200,
                        background: isDark ? 'rgba(13,16,25,0.98)' : '#ffffff',
                        border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #dbe7fb',
                        borderRadius: 14,
                        padding: 8,
                        boxShadow: isDark ? '0 16px 48px rgba(0,0,0,0.6)' : '0 12px 36px rgba(37,99,235,0.18)',
                      }}
                    >
                      <div style={{ padding: '8px 12px', borderBottom: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0', marginBottom: 4 }}>
                        <p style={{ fontSize: 11, color: theme.textMuted, marginBottom: 2 }}>Signed in as</p>
                        <p style={{ fontSize: 13, fontWeight: 600, color: theme.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</p>
                      </div>
                      <button
                        onClick={() => { setIsProfileMenuOpen(false); navigate('/profile'); }}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: 'none', border: 'none', color: theme.textSecondary, fontSize: 13, cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(37,99,235,0.08)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <UserIcon size={14} />
                        Profile
                      </button>
                      <button
                        onClick={() => { logout(); setIsProfileMenuOpen(false); navigate('/'); toast.success('Logged out'); }}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: 'none', border: 'none', color: '#f87171', fontSize: 13, cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(37,99,235,0.08)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <LogOut size={14} />
                        Logout
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ThemeToggle />
              <button
                data-testid="login-button"
                onClick={() => openLogin()}
                style={{ padding: '8px 18px', borderRadius: 100, background: isDark ? 'rgba(255,255,255,0.08)' : '#f8fafc', border: `1px solid ${theme.border}`, color: theme.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.14)' : '#f1f5f9'; e.currentTarget.style.color = theme.textPrimary; }}
                onMouseLeave={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.08)' : '#f8fafc'; e.currentTarget.style.color = theme.textSecondary; }}
              >
                Sign in
              </button>
            </div>
          )}
        </div>
      </header>

      <main style={{ maxWidth: 1120, margin: '0 auto', padding: '132px 24px 72px' }}>
        <article
          style={{
            maxWidth: 760,
            margin: '0 auto',
            padding: 0,
            fontFamily: "'Source Serif 4', 'Merriweather', Georgia, 'Times New Roman', serif",
          }}
        >
          <h1 style={{ fontFamily: "'Inter', 'Manrope', system-ui, sans-serif", fontSize: 'clamp(44px, 5.2vw, 56px)', lineHeight: 1.06, margin: 0, color: theme.textPrimary, fontWeight: 800, letterSpacing: '-0.02em' }}>Refund & Cancellation Policy</h1>
          <p style={{ marginTop: 18, fontSize: 20, color: theme.textMuted, fontWeight: 600 }}>Last updated: May 2026</p>

          <p style={{ marginTop: 60, fontSize: 18, lineHeight: 1.75, color: theme.textSecondary, fontWeight: 500 }}>
            At FairHire, we aim to provide transparent and valuable services to our users.
          </p>

          <h2 style={{ fontFamily: "'Inter', 'Manrope', system-ui, sans-serif", marginTop: 56, marginBottom: 14, fontSize: 44, fontWeight: 800, lineHeight: 1.2, color: theme.textPrimary }}>Subscription & Payments</h2>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, margin: 0 }}>
            Certain features of FairHire may require a paid subscription or one-time payment. By making a payment, you agree to the pricing, billing, and subscription terms displayed at the time of purchase.
          </p>

          <h2 style={{ fontFamily: "'Inter', 'Manrope', system-ui, sans-serif", marginTop: 56, marginBottom: 14, fontSize: 44, fontWeight: 800, lineHeight: 1.2, color: theme.textPrimary }}>Refund Policy</h2>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, margin: 0 }}>
            Due to the nature of digital services and instant access to platform features, all payments made to FairHire are generally non-refundable.
          </p>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, marginTop: 16, marginBottom: 0 }}>
            However, refunds may be considered in the following cases:
          </p>
          <ul style={{ marginTop: 16, marginBottom: 0, paddingLeft: 36, listStyleType: 'disc', color: theme.textSecondary, fontSize: 18, lineHeight: 1.7 }}>
            <li style={{ marginBottom: 8 }}>Duplicate payment</li>
            <li style={{ marginBottom: 8 }}>Failed transaction where money was deducted but service was not activated</li>
            <li style={{ marginBottom: 8 }}>Technical issues caused directly by FairHire that prevent access to purchased features</li>
            <li>
              If you don&apos;t find the insights helpful within 7 days, just email us at{' '}
              <a href="mailto:support@fairhire.in" style={{ color: isDark ? '#93c5fd' : '#2563eb', textDecoration: 'none', fontWeight: 600 }}>
                support@fairhire.in
              </a>
              {' '}and we&apos;ll refund your payment, no questions asked.
            </li>
          </ul>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, marginTop: 16, marginBottom: 0 }}>
            Refund requests must be raised within 7 days of the transaction.
          </p>

          <h2 style={{ fontFamily: "'Inter', 'Manrope', system-ui, sans-serif", marginTop: 56, marginBottom: 14, fontSize: 44, fontWeight: 800, lineHeight: 1.2, color: theme.textPrimary }}>Cancellation Policy</h2>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, margin: 0 }}>
            Users may cancel their subscription at any time.
          </p>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, marginTop: 16, marginBottom: 0 }}>After cancellation:</p>
          <ul style={{ marginTop: 16, marginBottom: 0, paddingLeft: 36, listStyleType: 'disc', color: theme.textSecondary, fontSize: 18, lineHeight: 1.7 }}>
            <li style={{ marginBottom: 8 }}>Your subscription benefits will remain active until the end of the current billing period</li>
            <li>No partial refunds will be provided for unused periods</li>
          </ul>

          <h2 style={{ fontFamily: "'Inter', 'Manrope', system-ui, sans-serif", marginTop: 56, marginBottom: 14, fontSize: 44, fontWeight: 800, lineHeight: 1.2, color: theme.textPrimary }}>Failed Transactions</h2>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, margin: 0 }}>
            If a payment is deducted but the service is not activated, please contact us with transaction details. We will verify the payment and resolve the issue as soon as possible.
          </p>

          <h2 style={{ fontFamily: "'Inter', 'Manrope', system-ui, sans-serif", marginTop: 56, marginBottom: 14, fontSize: 44, fontWeight: 800, lineHeight: 1.2, color: theme.textPrimary }}>Contact</h2>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, marginBottom: 8, marginTop: 0 }}>For refund, cancellation, or billing-related queries, contact:</p>
          <p style={{ fontSize: 18, lineHeight: 1.7, color: theme.textSecondary, margin: 0 }}>
            Email:{' '}
            <a href="mailto:support@fairhire.in" style={{ color: isDark ? '#93c5fd' : '#2563eb', textDecoration: 'none', fontWeight: 600 }}>
              support@fairhire.in
            </a>
          </p>
        </article>
      </main>

      <footer style={{ padding: '22px 24px', borderTop: `1px solid ${theme.border}`, background: theme.bg }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <p style={{ fontSize: 13, color: theme.textMuted, margin: 0 }}>© {currentYear} fairhire. All rights reserved.</p>
          <a href="mailto:support@fairhire.in" style={{ fontSize: 13, color: theme.textSecondary, textDecoration: 'none', fontWeight: 500 }}>support@fairhire.in</a>
        </div>
      </footer>
    </div>
  );
}
